// Phase 9.6 — Portal new-project request form (2026-05-12).
//
// `/request-project` — form to brief the rep on a new project the
// client wants to spin up. Submits to POST /portal/projects/request →
// inserts a row into `client_project_requests` for staff triage. Once
// submitted, the form shows a confirmation card with a link back to
// the dashboard.
//
// Past requests + their status (pending / converted / dismissed) show
// below the form so clients can see what's already in flight.
//
// 2026-10-19 (CEO): the client can attach files (logos, references, briefs —
// up to 10, 50 MB each) and tick items from "Re-order something we've made
// before". Both pickers only show once the server says they're switched on
// (GET /portal/projects/request/options); until then the plain form shows,
// with no error. The re-order list carries names and photos — never prices.
import { useRef, useState, type ChangeEvent, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  PRODUCT_CATEGORIES,
  STATUS_LABEL,
  STATUS_PILL_CLASS,
  fetchPortalProjectRequests,
  fetchRequestFormOptions,
  formatFileSize,
  submitPortalProjectRequest,
  uploadRequestFile,
  type ReorderOption,
} from "@/lib/portal-project-requests";
import { ApiError } from "@/lib/api";
import { useAuth } from "@/auth/AuthProvider";
import { PortalLayout } from "@/components/PortalLayout";

interface PickedFile {
  key: string;
  name: string;
  size: number;
  status: "uploading" | "done" | "error";
  fileId?: string;
}

/** Ticked re-order items: line item id → quantity text ("" = same as before / not sure). */
type ReorderPicks = Record<string, string>;

function formatDate(value: string | null): string {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

export function NewProjectRequestPage() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const [projectName, setProjectName] = useState("");
  const [productCategory, setProductCategory] = useState("");
  const [budgetRange, setBudgetRange] = useState("");
  const [deadline, setDeadline] = useState("");
  const [notes, setNotes] = useState("");
  const [submittedId, setSubmittedId] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [picked, setPicked] = useState<PickedFile[]>([]);
  const [fileError, setFileError] = useState<string | null>(null);
  const [reorder, setReorder] = useState<ReorderPicks>({});
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data: history } = useQuery({
    queryKey: ["portal-project-requests"],
    queryFn: fetchPortalProjectRequests,
  });

  const { data: options } = useQuery({
    queryKey: ["portal-project-request-options"],
    queryFn: fetchRequestFormOptions,
    staleTime: 5 * 60 * 1000,
  });
  const attachmentsOn = options?.attachments_enabled === true;
  const reorderItems = options?.reorder_enabled ? options.reorder_items : [];
  const maxFiles = options?.max_files ?? 10;
  const maxBytes = options?.max_file_bytes ?? 50 * 1024 * 1024;

  const uploadMutation = useMutation({ mutationFn: uploadRequestFile });

  function onFilesChosen(e: ChangeEvent<HTMLInputElement>): void {
    const chosen = Array.from(e.target.files ?? []);
    // Clear so the same file can be chosen again after removing it.
    e.target.value = "";
    if (chosen.length === 0) return;
    setFileError(null);
    const room = maxFiles - picked.length;
    if (room <= 0) {
      setFileError(`You can attach up to ${maxFiles} files.`);
      return;
    }
    const tooBig = chosen.filter((f) => f.size > maxBytes);
    const fits = chosen.filter((f) => f.size <= maxBytes && f.size > 0);
    const accepted = fits.slice(0, room);
    const problems: string[] = [];
    if (tooBig.length > 0) {
      problems.push(`${tooBig.map((f) => f.name).join(", ")} ${tooBig.length === 1 ? "is" : "are"} over ${formatFileSize(maxBytes)}.`);
    }
    if (fits.length > accepted.length) problems.push(`Only ${maxFiles} files can be attached.`);
    if (problems.length > 0) setFileError(problems.join(" "));

    for (const file of accepted) {
      const key = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      setPicked((prev) => [...prev, { key, name: file.name, size: file.size, status: "uploading" }]);
      uploadMutation
        .mutateAsync(file)
        .then(({ fileId }) => {
          setPicked((prev) => prev.map((p) => (p.key === key ? { ...p, status: "done", fileId } : p)));
        })
        .catch((err: unknown) => {
          setPicked((prev) => prev.map((p) => (p.key === key ? { ...p, status: "error" } : p)));
          // A refused file type says which types work (M3).
          const msg = err instanceof ApiError ? (err.body as { message?: string } | null)?.message : undefined;
          if (msg) setFileError(`${file.name}: ${msg}`);
        });
    }
  }

  function removeFile(key: string): void {
    setPicked((prev) => prev.filter((p) => p.key !== key));
  }

  function toggleReorder(id: string): void {
    setReorder((prev) => {
      const next = { ...prev };
      if (id in next) delete next[id];
      else next[id] = "";
      return next;
    });
  }

  const uploading = picked.some((p) => p.status === "uploading");

  const submitMutation = useMutation({
    mutationFn: submitPortalProjectRequest,
    onSuccess: (res) => {
      setSubmittedId(res.request_id);
      setFormError(null);
      // Reset form for the next request (rare, but handle it).
      setProjectName("");
      setProductCategory("");
      setBudgetRange("");
      setDeadline("");
      setNotes("");
      setPicked([]);
      setFileError(null);
      setReorder({});
      void queryClient.invalidateQueries({
        queryKey: ["portal-project-requests"],
      });
    },
    onError: (err) => {
      const errBody =
        err instanceof ApiError
          ? (err.body as { error?: string; message?: string } | null)
          : null;
      const code = errBody?.error;
      setFormError(
        (code === "file_missing" || code === "file_too_large" || code === "file_wrong_type") && errBody?.message
          ? errBody.message
          : code === "invalid_files"
          ? "One of your files couldn't be attached. Remove it and upload it again."
          : code === "invalid_reorder"
            ? "One of the items you picked can't be re-ordered here. Untick it and try again."
            : code === "extras_not_ready"
              ? "Attachments aren't switched on yet. Remove your files and picks, or send them to your account manager by email."
              : "Something went wrong submitting your request. Try again.",
      );
    },
  });

  function onSubmit(e: FormEvent): void {
    e.preventDefault();
    const trimmed = projectName.trim();
    if (trimmed.length === 0) {
      setFormError("Tell us the name or short description of the project.");
      return;
    }
    if (uploading) {
      setFormError("Wait for your files to finish uploading.");
      return;
    }
    if (picked.some((p) => p.status === "error")) {
      setFormError("A file didn't upload. Remove it or try it again.");
      return;
    }
    const reorderPicks = Object.entries(reorder).map(([id, qty]) => {
      const n = Number.parseInt(qty, 10);
      return { line_item_id: id, quantity: Number.isFinite(n) && n > 0 ? n : null };
    });
    const fileIds = picked.flatMap((p) => (p.fileId ? [p.fileId] : []));
    setFormError(null);
    submitMutation.mutate({
      project_name: trimmed,
      product_category: productCategory.trim() || null,
      budget_range: budgetRange.trim() || null,
      deadline: deadline || null,
      notes: notes.trim() || null,
      ...(fileIds.length > 0 ? { attachment_file_ids: fileIds } : {}),
      ...(reorderPicks.length > 0 ? { reorder_items: reorderPicks } : {}),
    });
  }

  if (!user) return null;
  const requests = history?.rows ?? [];

  return (
    <PortalLayout>
      {/* A readable form width, left-aligned in the full-width page (CEO 2026-10-19). */}
      <div className="max-w-3xl">
        <Link
          to="/dashboard"
          className="mb-3 inline-block text-sm font-semibold text-ink underline-offset-2 hover:underline"
        >
          ← Dashboard
        </Link>
        <h1 className="mb-2 font-display text-4xl font-bold tracking-tight">
          Start a new project
        </h1>
        <p className="mb-10 text-brown-400">
          Tell us what you're thinking. Your account manager will follow up
          within a business day to scope it together.
        </p>

        {submittedId ? (
          <section className="mb-10 rounded-lg border border-peach-300 bg-peach-50 p-8 shadow-sm">
            <h2 className="mb-2 font-display text-2xl font-bold tracking-tight">
              Got it. We're on it.
            </h2>
            <p className="mb-6 text-brown-500">
              Your request is in the queue. Your account manager will reach
              out shortly to scope it with you.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link
                to="/dashboard"
                className="rounded-md bg-ink px-6 py-2.5 font-semibold text-white shadow-sm transition-colors hover:bg-ink-700"
              >
                Back to dashboard
              </Link>
              <button
                type="button"
                onClick={() => setSubmittedId(null)}
                className="rounded-md border border-cream-300 bg-white px-6 py-2.5 font-semibold text-brown-500 shadow-sm transition-colors hover:border-brown-300"
              >
                Submit another
              </button>
            </div>
          </section>
        ) : (
          <form
            onSubmit={onSubmit}
            className="mb-10 rounded-lg border border-cream-200 bg-white p-8 shadow-sm"
          >
            <label className="mb-5 block">
              <span className="mb-2 block text-sm font-semibold text-brown-500">
                Project name <span className="text-rust-500">*</span>
              </span>
              <input
                autoFocus
                required
                type="text"
                maxLength={200}
                value={projectName}
                onChange={(e) => setProjectName(e.target.value)}
                placeholder="e.g. New-hire onboarding kits, Q3 client gifts"
                className="w-full rounded-md border border-cream-400 bg-white px-4 py-3 text-brown-500 placeholder:text-[#8a8a85] shadow-sm focus:border-peach-400 focus:outline-none focus:ring-0"
              />
            </label>

            <label className="mb-5 block">
              <span className="mb-2 block text-sm font-semibold text-brown-500">
                Product category
              </span>
              <select
                value={productCategory}
                onChange={(e) => setProductCategory(e.target.value)}
                className="w-full rounded-md border border-cream-400 bg-white px-4 py-3 text-brown-500 shadow-sm focus:border-peach-400 focus:outline-none"
              >
                <option value="">Not sure yet</option>
                {PRODUCT_CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </label>

            <div className="mb-5 grid gap-5 sm:grid-cols-2">
              <label className="block">
                <span className="mb-2 block text-sm font-semibold text-brown-500">
                  Budget range
                </span>
                <input
                  type="text"
                  maxLength={120}
                  value={budgetRange}
                  onChange={(e) => setBudgetRange(e.target.value)}
                  placeholder="e.g. $2k–5k, ~$10k total"
                  className="w-full rounded-md border border-cream-400 bg-white px-4 py-3 text-brown-500 placeholder:text-[#8a8a85] shadow-sm focus:border-peach-400 focus:outline-none"
                />
              </label>
              <label className="block">
                <span className="mb-2 block text-sm font-semibold text-brown-500">
                  When do you need it?
                </span>
                <input
                  type="date"
                  value={deadline}
                  onChange={(e) => setDeadline(e.target.value)}
                  className="w-full rounded-md border border-cream-400 bg-white px-4 py-3 text-brown-500 shadow-sm focus:border-peach-400 focus:outline-none"
                />
              </label>
            </div>

            <label className="mb-5 block">
              <span className="mb-2 block text-sm font-semibold text-brown-500">
                Notes
              </span>
              <textarea
                maxLength={4000}
                rows={5}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Quantities you're thinking, the audience, any references or brand constraints…"
                className="w-full rounded-md border border-cream-400 bg-white px-4 py-3 text-brown-500 placeholder:text-[#8a8a85] shadow-sm focus:border-peach-400 focus:outline-none focus:ring-0"
              />
              <span className="mt-1 block text-xs text-brown-300">
                {notes.length} / 4000
              </span>
            </label>

            {reorderItems.length > 0 && (
              <ReorderPicker
                items={reorderItems}
                picks={reorder}
                onToggle={toggleReorder}
                onQuantity={(id, qty) =>
                  setReorder((prev) => ({ ...prev, [id]: qty }))
                }
              />
            )}

            {attachmentsOn && (
              <div className="mb-5">
                <span className="mb-1 block text-sm font-semibold text-brown-500">
                  Files
                </span>
                <p className="mb-3 text-xs text-brown-300">
                  Logos, references or a brief — up to {maxFiles} files,{" "}
                  {formatFileSize(maxBytes)} each.
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  onChange={onFilesChosen}
                  className="hidden"
                />
                {picked.length > 0 && (
                  <ul className="mb-3 divide-y divide-cream-100 rounded-md border border-cream-200">
                    {picked.map((p) => (
                      <li
                        key={p.key}
                        className="flex items-center justify-between gap-3 px-4 py-2 text-sm"
                      >
                        <span className="min-w-0 truncate text-brown-500">
                          {p.name}
                          <span className="ml-2 text-xs text-brown-300">
                            {formatFileSize(p.size)}
                          </span>
                        </span>
                        <span className="flex shrink-0 items-center gap-3 text-xs">
                          {p.status === "uploading" && (
                            <span className="text-brown-300">Uploading…</span>
                          )}
                          {p.status === "done" && (
                            <span className="text-brown-400">Attached</span>
                          )}
                          {p.status === "error" && (
                            <span className="text-rust-500">Didn't upload</span>
                          )}
                          <button
                            type="button"
                            onClick={() => removeFile(p.key)}
                            className="font-semibold text-brown-400 underline-offset-2 hover:text-brown-500 hover:underline"
                          >
                            Remove
                          </button>
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
                {picked.length < maxFiles && (
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="rounded-md border border-cream-300 bg-white px-4 py-2 text-sm font-semibold text-brown-500 shadow-sm transition-colors hover:border-brown-300"
                  >
                    {picked.length === 0 ? "Attach files" : "Attach more"}
                  </button>
                )}
                {fileError && (
                  <p className="mt-2 text-xs text-rust-500">{fileError}</p>
                )}
              </div>
            )}

            {formError && (
              <div className="mb-4 rounded-md border border-rust-200 bg-rust-50 px-4 py-3 text-sm text-rust-500">
                {formError}
              </div>
            )}

            <button
              type="submit"
              disabled={submitMutation.isPending || uploading}
              className="rounded-md bg-ink px-6 py-3 font-semibold text-white shadow-sm transition-colors hover:bg-ink-700 disabled:opacity-50"
            >
              {submitMutation.isPending
                ? "Submitting…"
                : uploading
                  ? "Uploading files…"
                  : "Submit request"}
            </button>
          </form>
        )}

        {requests.length > 0 && (
          <section>
            <h2 className="mb-4 font-display text-xl font-bold tracking-tight">
              Your past requests
            </h2>
            <div className="overflow-hidden rounded-lg border border-cream-200 bg-white shadow-sm">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-cream-200 text-xs font-semibold uppercase tracking-wider text-brown-300">
                    <th className="px-6 py-3">Submitted</th>
                    <th className="px-6 py-3">Project</th>
                    <th className="px-6 py-3">Category</th>
                    <th className="px-6 py-3">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {requests.map((r) => (
                    <tr
                      key={r.id}
                      className="border-b border-cream-100 last:border-b-0"
                    >
                      <td className="px-6 py-3 text-brown-400">
                        {formatDate(r.createdAt)}
                      </td>
                      <td className="px-6 py-3 text-brown-500">
                        <div className="font-semibold">{r.projectName}</div>
                        {r.deadline && (
                          <div className="mt-0.5 text-xs text-brown-300">
                            Needs by {formatDate(r.deadline)}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-3 text-brown-400">
                        {r.productCategory ?? "—"}
                      </td>
                      <td className="px-6 py-3">
                        <span
                          className={
                            "inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold " +
                            STATUS_PILL_CLASS[r.status]
                          }
                        >
                          {STATUS_LABEL[r.status]}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}
      </div>
    </PortalLayout>
  );
}

/** "Re-order something we've made before" — products from this client's
 *  finished orders. Tick an item, optionally say how many. No prices here. */
function ReorderPicker({
  items,
  picks,
  onToggle,
  onQuantity,
}: {
  items: ReorderOption[];
  picks: ReorderPicks;
  onToggle: (id: string) => void;
  onQuantity: (id: string, qty: string) => void;
}) {
  return (
    <div className="mb-5">
      <span className="mb-1 block text-sm font-semibold text-brown-500">
        Re-order something we've made before
      </span>
      <p className="mb-3 text-xs text-brown-300">
        Optional. Tick anything you'd like again; add a quantity if you know it.
      </p>
      <div className="grid max-h-96 gap-2 overflow-y-auto pr-1 sm:grid-cols-2">
        {items.map((item) => {
          const on = item.line_item_id in picks;
          return (
            <div
              key={item.line_item_id}
              className={
                "flex items-center gap-3 rounded-md border p-2 transition-colors " +
                (on
                  ? "border-peach-400 bg-peach-50"
                  : "border-cream-200 bg-white hover:border-cream-400")
              }
            >
              <label className="flex min-w-0 flex-1 cursor-pointer items-center gap-3">
                <input
                  type="checkbox"
                  checked={on}
                  onChange={() => onToggle(item.line_item_id)}
                  className="h-4 w-4 shrink-0 accent-rust-500"
                />
                {item.image_url ? (
                  <img
                    src={item.image_url}
                    alt=""
                    loading="lazy"
                    className="h-12 w-12 shrink-0 rounded bg-cream-100 object-contain"
                  />
                ) : (
                  <span className="h-12 w-12 shrink-0 rounded bg-cream-100" />
                )}
                <span className="min-w-0">
                  <span className="block truncate text-sm font-semibold text-brown-500">
                    {item.name}
                  </span>
                  <span className="block text-xs text-brown-300">
                    Last time: {item.last_quantity} on #{item.last_order_number}
                  </span>
                </span>
              </label>
              {on && (
                <input
                  type="number"
                  min={1}
                  inputMode="numeric"
                  value={picks[item.line_item_id]}
                  onChange={(e) => onQuantity(item.line_item_id, e.target.value)}
                  placeholder="Qty"
                  aria-label={`Quantity for ${item.name}`}
                  className="w-20 shrink-0 rounded-md border border-cream-400 bg-white px-2 py-1.5 text-sm text-brown-500 focus:border-peach-400 focus:outline-none"
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
